import type Result from 'true-myth/result'

export interface AutoLoginDeps {
    /** Current session state — skip the whole chain if already logged in (mirror warm). */
    isLoggedIn: boolean
    /** One-Tap (the only step with UI) runs on the homepage only. */
    isHomepage: boolean
    /** Restore + refresh an expired cognito mirror. Silent (no UI). Resolves true if a session is now active. */
    restoreSession: () => Promise<boolean>
    /** Prompt Google One-Tap and establish a session. Resolves true if logged in. */
    oneTapRestore: () => Promise<boolean>
}

/**
 * Adapt a `Result`-returning attempt to the chain's step contract. A failure becomes the
 * same fall-through as "this step established no session", after `onFailure` sees the
 * error — the chain treats the two alike, but a silent restore that is failing for every
 * returning visitor should not also be invisible.
 */
export function asChainStep<E>(
    attempt: () => Promise<Result<boolean, E>>,
    onFailure: (error: E) => void,
): () => Promise<boolean> {
    return async () => {
        const outcome = await attempt()
        if (outcome.isErr) {
            onFailure(outcome.error)
            return false
        }
        return outcome.value
    }
}

/**
 * Silent auto-login chain decision logic. Runs only when logged-out, in order: mirror
 * restore/refresh → Google One-Tap, stopping at the first that establishes a session.
 * Mirror restore is silent and runs on every route; One-Tap shows UI, so it is gated to
 * the homepage.
 *
 * A step resolves `false` for every way it can fail — that is the contract, and it is what
 * keeps a failing provider from breaking the page. Nothing here catches: a step that broke
 * that contract and rejected would escape as an unhandled rejection, which is loud, where
 * swallowing it would report a broken subsystem as a visitor who simply has no session.
 */
export async function runAutoLoginChain(deps: AutoLoginDeps): Promise<void> {
    if (deps.isLoggedIn) {
        return
    }
    if (await deps.restoreSession()) {
        return
    }
    if (deps.isHomepage) {
        await deps.oneTapRestore()
    }
}
